import CustomDatepickerSelected from 'components/common/CustomDatepickerSelected';
import CalendarIcon from 'components/common/Icons/CalendarIcon';
import { BACKSPACE_CODE, DELETE_CODE } from 'components/common/func';
import { checkFormInputValidation } from 'features/pii/func';
import { datePicker, formatter } from 'helpers';
import { Fade } from 'helpers/animation';
import useFormInput from 'hooks/form/useFormInput';
import useLang from 'hooks/useLang';
import { useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';
import { paragraph500Mixin } from 'styles';

const FormInputDatepickerValidation = ({
  ref,
  field,
  information,
  setInformation,
  validation,
  setValidation,
}) => {
  const { t } = useLang();

  const [isShowDatePickerIcon, setShowDatePickerIcon] = useState(true);
  const [isOpenDatePicker, setOpenDatePicker] = useState(false);
  const [datePickerISO, setDatePickerISO] = useState(null);
  const [dateMaskStr, setDateMaskStr] = useState(null);

  const dateFormat = formatter.formatParse(datePicker.dateFormat);

  const {
    resetFormInput,
    setInformationFormInput,
    setValidationFormInput,
    getValueTrim,
    FormLabel,
    FormControlFeedback,
  } = useFormInput(field);

  useEffect(() => {
    if (information[field.name]) {
      const dateISO = datePicker.dateUTCToDateISO(information[field.name]);
      setDatePickerISO(dateISO);

      const { dateMask } = datePicker.getDateMask('', information[field.name]);
      setDateMaskStr(formatter.mask(dateMask, dateFormat));
    }
  }, [information]);

  const handleOnChange = (dateUTC) => {
    let newInfomation = { ...information };
    let newValidation = { ...validation };

    const dateISO = datePicker.dateUTCToDateISO(dateUTC);
    const dateMask = datePicker.dateUTCToDateStr(dateUTC)?.split('/')?.join('');

    setDatePickerISO(dateISO);

    setDateMaskStr(dateMask);

    newInfomation = setInformationFormInput({
      value: datePicker.getDateUTC(
        dateUTC.getDate(),
        dateUTC.getMonth(),
        dateUTC.getFullYear()
      ),
      information: newInfomation,
      setInformation,
    });

    setValidationFormInput({
      validation: newValidation,
      setValidation,
      checkFormInputValidation: () =>
        checkFormInputValidation(
          newInfomation[field.name],
          field,
          newInfomation,
          newValidation
        ),
    });

    setOpenDatePicker(false);
  };

  const handleInputOnChange = (e) => {
    const { value } = e.target;
    let valueTrim = getValueTrim(value);

    if (valueTrim?.length > 10) return;

    let newValidation = { ...validation };

    setShowDatePickerIcon(true);

    if (!valueTrim) {
      let newInformation = setInformationFormInput({
        value: '',
        information,
        setInformation,
      });
      setValidationFormInput({
        validation,
        setValidation,
        checkFormInputValidation: () =>
          checkFormInputValidation(
            newInformation[name],
            field,
            newInformation,
            validation
          ),
      });
      setDateMaskStr('');
      return;
    }

    const { dateMask, dd, MM, yyyy } = datePicker.getDateMask(valueTrim);
    setDateMaskStr(formatter.mask(dateMask, dateFormat));

    newValidation = {
      ...newValidation,
      [field.name]: '',
    };

    if (valueTrim.length === 10) {
      const dateUTC = datePicker.getDateUTC(dd, MM - 1, yyyy);
      const dateISO = datePicker.dateUTCToDateISO(dateUTC);
      const currentYear = new Date(dateISO).getFullYear();

      if (
        datePicker.minYear > currentYear ||
        datePicker.maxYear < currentYear
      ) {
        setShowDatePickerIcon(false);

        setDatePickerISO(null);
      } else {
        setShowDatePickerIcon(true);

        setDatePickerISO(dateISO);
      }

      setInformationFormInput({
        value: dateUTC,
        information,
        setInformation,
      });

      const isOfAgeToUse = datePicker.getOfAgeToUse(dateUTC);
      if (!isOfAgeToUse) {
        newValidation = {
          ...newValidation,
          [field.name]: field.msgInvalid,
        };
      }
    }

    setValidation(newValidation);
  };

  const handleInputOnKeyDown = (e) => {
    if ([BACKSPACE_CODE, DELETE_CODE].includes(e.which)) {
      resetFormInput({
        information,
        setInformation,
        validation,
        setValidation,
      });
      setDateMaskStr('');
      setShowDatePickerIcon(true);
    }
  };

  const handleOnToggleDatePicker = () => {
    setOpenDatePicker(!isOpenDatePicker);
  };

  return (
    <FormGroupStyled>
      <FormLabel styles={{ marginBottom: '4px' }} />
      <FormControl
        ref={ref}
        type={field.type}
        name={field.name}
        value={formatter.mask(dateMaskStr, dateFormat)}
        placeholder={field.placeholder}
        onClick={() => setOpenDatePicker(false)}
        onChange={handleInputOnChange}
        onKeyDown={handleInputOnKeyDown}
        isInvalid={validation[field.name]}
        readOnly={field?.isReadOnly}
        inputMode="numeric"
      />
      {isShowDatePickerIcon && (
        <CalendarIcon
          name={field.name}
          color="var(--ds-c-grey-dark)"
          onClick={handleOnToggleDatePicker}
          styles={{
            position: 'absolute',
            top: '35px',
            right: '12px',
            cursor: 'pointer',
          }}
        />
      )}
      {isOpenDatePicker && (
        <CustomDatepickerSelected
          isOpen={isOpenDatePicker}
          date={datePickerISO}
          onChange={handleOnChange}
          onClose={handleOnToggleDatePicker}
        />
      )}
      <FormControlFeedback
        value={t(validation[field.name])}
        validationStyles={{ position: 'unset' }}
      />
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  ${Fade};
  position: relative;
`;

const FormControl = styled(Form.Control)`
  ${paragraph500Mixin};
  text-transform: uppercase;
  height: 44px;
  border: 1px solid var(--ds-c-grey-light);
  border-radius: 8px;
  padding-inline: 10px;
  padding-right: 38px;
  background: var(--ds-c-white);

  &:focus {
    border: 1px solid var(--ds-c-grey-dark) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus,
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
  }

  &[readonly] {
    background: var(--ds-c-white);
  }

  &::placeholder {
    ${paragraph500Mixin};
    color: var(--ds-c-blue-disabled) !important;
    text-transform: uppercase;
  }

  &[type='date']::-webkit-calendar-picker-indicator {
    background: none !important;
  }
`;

export default FormInputDatepickerValidation;
